import { apiRequest } from "util/services";

/** ReviewItem.updateAssociations parity */
export function updateDrawerTopicAssociations(sequences, newSequences) {
  const base = Array.isArray(sequences) ? sequences : [];
  const incoming = Array.isArray(newSequences) ? newSequences : [];
  const newSequencesMap = new Map();
  incoming.forEach((seq) => {
    if (seq?.seq_id) {
      newSequencesMap.set(seq.seq_id, seq);
    }
  });

  return base.map((seq) => {
    const newSeq = newSequencesMap.get(seq.seq_id);
    if (!newSeq) {
      return seq;
    }
    const newTopic = newSeq.topic_associations?.[0];
    const updatedTopicAssociations = (seq.topic_associations || []).map((topic) =>
      newTopic
        ? {
            ...topic,
            sentiment: newTopic.sentiment,
            intent: newTopic.intent,
          }
        : topic,
    );
    return {
      ...seq,
      isAnnotated: true,
      topic_associations: updatedTopicAssociations,
    };
  });
}

function syncTopicEditsOnSequences(allSeq, newSequences) {
  const updated = (allSeq || []).map((sequence) => ({
    ...sequence,
    topic_associations: (sequence.topic_associations || []).map((topic) => ({
      ...topic,
    })),
  }));

  updated.forEach((sequence) => {
    (newSequences || []).forEach((newSequence) => {
      (newSequence.topic_associations || []).forEach((newTopic) => {
        const matchingTopic = sequence.topic_associations.find(
          (topic) => String(topic.id) === String(newTopic.id),
        );
        if (matchingTopic) {
          matchingTopic.sentiment = newTopic.sentiment;
          matchingTopic.intent = newTopic.intent;
        }
      });
    });
  });

  return updated;
}

function patchTopicOnSequences(sequences, topic, sentiment, intent) {
  const topicId = String(topic?.id ?? "");
  if (!Array.isArray(sequences) || !topicId) return [];
  return sequences.map((seq) => ({
    ...seq,
    isAnnotated: true,
    topic_associations: (seq.topic_associations || []).map((ta) =>
      String(ta?.id) === topicId
        ? {
            ...ta,
            sentiment,
            intent,
            label: topic.label,
            type: topic.type || ta.type,
          }
        : ta,
    ),
  }));
}

function buildNewSequences(dateGpid, highlightedSpans, topic, sentiment, intent) {
  const spans = Array.isArray(highlightedSpans) ? highlightedSpans : [];
  const topicsToPayload = [
    {
      id: topic.id,
      label: topic.label,
      type: topic.type ? topic.type : "custom",
      sentiment,
      intent,
    },
  ];

  return spans.map((highlight) => ({
    start_index: highlight.indices[0],
    end_index: highlight.indices[1],
    text: highlight.text,
    seq_id: `${dateGpid} ⌀ ${highlight.indices[0]}_${highlight.indices[1]}`,
    date: new Date().toISOString(),
    isAnnotated: true,
    topic_associations: topicsToPayload,
  }));
}

function buildFinalTopics(relatedTopics, topicAssociations, allSeq) {
  const filteredTopics = (allSeq || [])
    .map((seq) =>
      (seq.topic_associations || []).map((association) =>
        String(association.id),
      ),
    )
    .flat();

  const finalTopics = relatedTopics?.length
    ? [
        ...relatedTopics.map((related) => String(related.id)),
        ...topicAssociations.map((association) => String(association.id)),
      ]
    : topicAssociations.map((association) => String(association.id));

  return [...new Set([...finalTopics, ...filteredTopics])];
}

/**
 * Build dashboards/v2/feedback payload — ReviewItem.handleApplyChanges parity
 * for isolated topic edits in Welcome / KPI side drawers.
 */
export function buildDrawerTopicFeedbackPayload({
  doc,
  sequences,
  classifierSequences,
  relatedTopics,
  highlightedSpans,
  topic,
  sentiment,
  intent,
  language,
}) {
  const dashboardId = doc?.dash_id ?? doc?.dashboard_id;
  const dateGpid = doc?.date_gpid;
  if (!dashboardId || !dateGpid || topic?.id == null) {
    return { error: "missing_scope" };
  }

  const topicAssociations = [
    {
      id: topic.id,
      label: topic.label,
      type: topic.type ? topic.type : "custom",
      sentiment,
      intent,
    },
  ];

  const baseSequences = Array.isArray(sequences) ? sequences : [];
  const newSequences = buildNewSequences(
    dateGpid,
    highlightedSpans,
    topic,
    sentiment,
    intent,
  );

  let allSeq =
    newSequences.length > 0
      ? updateDrawerTopicAssociations(baseSequences, newSequences)
      : patchTopicOnSequences(baseSequences, topic, sentiment, intent);

  if (newSequences.length > 0) {
    allSeq = syncTopicEditsOnSequences(allSeq, newSequences);
  }

  const classifier = Array.isArray(classifierSequences)
    ? classifierSequences
    : [];
  const finalSequences =
    classifier.length > 0 ? [...allSeq, ...classifier] : allSeq;

  const payload = {
    date_gpid: dateGpid,
    dashboard_id: dashboardId,
    language: language || "en",
    final_topics: buildFinalTopics(relatedTopics, topicAssociations, allSeq),
  };

  if (finalSequences.length > 0) {
    payload.sequences = finalSequences;
  }

  const internalId = doc?.internalId || doc?.internal_id;
  if (internalId) {
    payload.internal_id = internalId;
  }

  return payload;
}

/** ReviewItem.getIntersectedSeqeunces parity (classifier overlap). */
export function getIntersectedDrawerSequences(sequences, newSequence) {
  if (!Array.isArray(sequences) || sequences.length === 0) {
    return [];
  }
  return sequences.filter(
    (seq) =>
      parseInt(newSequence.start_index, 10) <= parseInt(seq.end_index, 10) &&
      parseInt(newSequence.end_index, 10) >= parseInt(seq.start_index, 10),
  );
}

/**
 * Build dashboards/v2/feedback payload for classifier (Publish / Case / Action)
 * edits — ReviewItem.handleApplyChanges classifier branch parity.
 */
export function buildDrawerClassifierFeedbackPayload({
  doc,
  sequences,
  classifierSequences,
  relatedTopics,
  highlightedSpans,
  clickedDecision,
  classifierValues = {},
  language,
}) {
  const dashboardId = doc?.dash_id ?? doc?.dashboard_id;
  const dateGpid = doc?.date_gpid;
  if (!dashboardId || !dateGpid) {
    return { error: "missing_scope" };
  }

  const spans = Array.isArray(highlightedSpans) ? highlightedSpans : [];
  if (!spans.length) {
    return { error: "missing_highlight" };
  }

  let newSequences = spans.map((highlight) => ({
    start_index: highlight.indices[0],
    end_index: highlight.indices[1],
    text: highlight.text,
    seq_id: `${dateGpid} ⌀ ${highlight.indices[0]}_${highlight.indices[1]}`,
    date: new Date().toISOString(),
    isForClassifier: true,
    isAnnotated: true,
    classifier: {
      mPublish: classifierValues.mPublish ?? null,
      mOpenCase: classifierValues.mOpenCase ?? null,
      mAction: classifierValues.mAction ?? null,
    },
  }));

  const baseClassifier = Array.isArray(classifierSequences)
    ? classifierSequences
    : [];
  const intersectedSequences = newSequences.map((sequence) =>
    getIntersectedDrawerSequences(baseClassifier, sequence),
  );
  const flatMapOfIntersections = intersectedSequences.flat();
  const filteredSeq = baseClassifier.filter(
    (seq) => !flatMapOfIntersections.includes(seq),
  );

  if (clickedDecision) {
    newSequences = newSequences.map((seq) => {
      const existingSeq = baseClassifier.find(
        (cs) =>
          parseInt(cs.start_index, 10) === parseInt(seq.start_index, 10) &&
          parseInt(cs.end_index, 10) === parseInt(seq.end_index, 10),
      );
      if (!existingSeq) {
        return seq;
      }
      const classifier = { ...existingSeq.classifier };
      if (["Publish", "Do not Publish"].includes(clickedDecision)) {
        classifier.mPublish = classifierValues.mPublish ?? null;
      } else if (
        ["Open a case", "Do not open a case"].includes(clickedDecision)
      ) {
        classifier.mOpenCase = classifierValues.mOpenCase ?? null;
      } else {
        classifier.mAction = classifierValues.mAction ?? null;
      }
      return { ...seq, classifier };
    });
  }

  const topicSeq = Array.isArray(sequences) ? sequences : [];
  const allSeq = filteredSeq
    ? topicSeq.length > 0
      ? [...filteredSeq, ...newSequences, ...topicSeq]
      : [...filteredSeq, ...newSequences]
    : topicSeq.length > 0
      ? [...newSequences, ...topicSeq]
      : newSequences;

  const payload = {
    date_gpid: dateGpid,
    dashboard_id: dashboardId,
    language: language || "en",
    sequences: allSeq,
    final_topics: (relatedTopics || []).map((topic) => String(topic.id)),
  };

  const internalId = doc?.internalId || doc?.internal_id;
  if (internalId) {
    payload.internal_id = internalId;
  }

  return payload;
}

/**
 * Same endpoint as Dashboard ReviewsList (`dashboards/v2/feedback`).
 */
export async function applyDrawerTopicFeedback(params) {
  const payload = buildDrawerTopicFeedbackPayload(params);
  if (payload.error) {
    return payload;
  }
  return apiRequest("dashboards/v2/feedback", "POST", payload, true);
}

export async function applyDrawerClassifierFeedback(params) {
  const payload = buildDrawerClassifierFeedbackPayload(params);
  if (payload.error) {
    return payload;
  }
  return apiRequest("dashboards/v2/feedback", "POST", payload, true);
}
